export const MONTHLY_PLAN_VERSION = "2026-10-23.1";
export const MONTHLY_PLAN_SERVICE = "bliss_monthly_prepayment";
export type PaymentFrequency = "monthly" | "biweekly";
export function installmentCount(
  frequency: PaymentFrequency,
  months: 3 | 6 | 9 | 12 = 6,
) {
  if (!["monthly", "biweekly"].includes(frequency) || ![3, 6, 9, 12].includes(months))
    throw Error("Choose a 3-, 6-, 9-, or 12-month payment plan.");
  return frequency === "biweekly" ? Math.floor(months * 26 / 12) : months;
}
export function prepaymentInstallments(
  totalCents: number,
  frequency: PaymentFrequency = "monthly",
  months: 3 | 6 | 9 | 12 = 6,
  upfrontCents = 0,
) {
  if (
    !Number.isSafeInteger(totalCents) ||
    totalCents < 600 ||
    totalCents > 99999999
  )
    throw Error("A confirmed project total is required.");
  const count = installmentCount(frequency, months);
  if (
    ![3, 6, 9, 12].includes(months) ||
    !Number.isSafeInteger(upfrontCents) ||
    upfrontCents < 0 ||
    totalCents - upfrontCents < count * 50
  )
    throw Error(
      "Choose an upfront payment that leaves at least $0.50 per installment.",
    );
  totalCents -= upfrontCents;
  const regular = Math.min(
    Math.round(totalCents / count),
    Math.floor((totalCents - 50) / (count - 1)),
  );
  return [
    ...Array(count - 1).fill(regular),
    totalCents - regular * (count - 1),
  ];
}
export function validInstallationMonth(
  value: unknown,
  months: 3 | 6 | 9 | 12 = 6,
  now = new Date(),
) {
  if (
    ![3, 6, 9, 12].includes(months) ||
    typeof value !== "string" ||
    !/^20\d{2}-(0[1-9]|1[0-2])$/.test(value)
  )
    return false;
  const minimum = new Date(
    Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + months, 1),
  );
  return value >= minimum.toISOString().slice(0, 7);
}
export function prepaymentTerms(
  frequency: PaymentFrequency,
  months: 3 | 6 | 9 | 12 = 6,
  upfrontCents = 0,
) {
  return [
    `You authorize Bliss Bedrooms, through Stripe, to save your card and automatically collect the ${installmentCount(frequency, months)} payments shown. ${upfrontCents > 0 ? "Your upfront payment is collected in checkout. Installments begin one payment interval later;" : "The first is collected after card setup;"} the rest are collected ${frequency === "biweekly" ? "every two weeks" : "monthly"}. Automatic billing ends after the final installment.`,
    "Bliss charges no interest or installment-plan fee. Your card issuer’s terms still apply. These payments cover the quoted scope shown; no tax is added. Separately approved changes are additional and must be settled before installation.",
    "This installment schedule replaces the standard 50% / 50% payment schedule for this plan. The entire project balance must be paid before delivery and installation. Your requested installation month is subject to designer review, fabrication timing and availability.",
    "If a payment fails or needs bank authentication, you must resolve it with Bliss before the project proceeds. A completed schedule alone is not proof of full payment.",
    "If plans change, payments cover documented design time, completed work and committed costs. Any amount due back reflects those costs and applicable credits. Contact Bliss to change or stop future installments; an approved change requires an updated written schedule.",
  ];
}

export function firstInstallmentAfterUpfront(
  created: number,
  frequency: PaymentFrequency,
) {
  const date = new Date(created * 1000);
  if (frequency === "biweekly") date.setUTCDate(date.getUTCDate() + 14);
  else {
    const day = date.getUTCDate();
    date.setUTCDate(1);
    date.setUTCMonth(date.getUTCMonth() + 1);
    const last = new Date(
      Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + 1, 0),
    ).getUTCDate();
    date.setUTCDate(Math.min(day, last));
  }
  return Math.floor(date.getTime() / 1000);
}

